import React, {useEffect, useState} from 'react';
import {IFormFieldTriggerConfig} from '../../../@interfaces/components/Form/IFormConfig';
import {useFormContext} from 'react-hook-form';
import app from '../../../helpers/app';
import classnames from '../../../helpers/classnames';
import {FormFieldTriggerAction, FormFieldTriggerConditionEnum} from '../../../@types/components/Form';

type Props<TProps> = TProps & {
    trigger?: IFormFieldTriggerConfig,
    componentToRender: React.ComponentType<TProps & { disabled?: boolean }>
    children?: unknown,
    forwardRef?: React.Ref<any>,
    wrapperTag?: string | React.ComponentType<{ className?: string }>,
    wrapperClassName?: string,
};

export default function TriggerableField<TProps>(props: Props<TProps>) {
    const {
        trigger,
        componentToRender,
        children,
        forwardRef,
        wrapperTag: Wrap = 'div',
        wrapperClassName,
        ...rest
    } = props;

    const dot = app('dot-accessor');
    const {watch, setValue} = useFormContext();
    const [state, setState] = useState<{
        disabled?: boolean,
        hide?: boolean,
    }>({});

    useEffect(() => {
        if (!trigger) {
            return;
        }

        const subscription = watch(value => {
            const filedValue = dot.get(value, trigger.field, undefined);
            const doAction = (trigger.condition === FormFieldTriggerConditionEnum.CHECKED) === (filedValue === true);

            if ([FormFieldTriggerAction.SHOW, FormFieldTriggerAction.HIDE].includes(trigger.action)) {
                setState({hide: (trigger.action === FormFieldTriggerAction.HIDE) === doAction});
            } else if ([FormFieldTriggerAction.ENABLE, FormFieldTriggerAction.DISABLE].includes(trigger.action)) {
                setState({disabled: (trigger.action === FormFieldTriggerAction.DISABLE) === doAction});
            } else if (trigger.action === FormFieldTriggerAction.EMPTY && doAction) {
                setValue(trigger.field, null);
            }
        });

        return () => subscription.unsubscribe();
    }, [dot, setValue, trigger, watch]);

    const Component = componentToRender as any;

    return <Wrap className={classnames('triggerable-field', wrapperClassName, {
        'd-none': state.hide,
        'is-invalid': String('className' in rest ? rest.className : '').includes('is-invalid')
    })}>
        <Component
                ref={forwardRef}
                {...rest}
                disabled={dot.get(rest, 'disabled', false) || state.disabled}
        >{children}</Component>
    </Wrap>;
}
